import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { api, ApiError, buildQueryString } from '@/lib/api'
import { briefingStoryContextQueryKeys } from './briefing-story-context'
import { storyQueryKeys } from './stories'

// ─── KAN-661 R33 — relevance inputs invalidate the contextual read ───────────
//
// The briefing-context panel on story detail is headed "Matches your current
// settings". Its contents are computed server-side from the reader's CURRENT
// industry, jurisdiction, tracked vendors and keyword watches — the same four
// things the mutations below change. If a reader untracks the vendor that put a
// story in their briefing and the panel goes on asserting the match, the
// heading is simply false.
//
// This estate has shipped that exact defect once: the Art. 21 replay objection
// was STORED under one query key and ENFORCED from another, so the opt-out only
// took effect after a page reload (error log 2026-07-26). The recorded lesson
// is that two React Query keys for one fact is a latent staleness bug wherever
// it appears. The contextual query is a second key over a derived view of the
// same fact, so it is invalidated from the same `onSuccess` — one helper, so
// the pairing cannot be half-applied to a future mutation.
//
// `useAcknowledgeAnalyticsNotice` deliberately does NOT use this: acknowledging
// the analytics notice changes nothing about relevance, and invalidating a
// contextual read from it would fire a token-bearing POST for no reason.
function invalidateRelevanceInputs(qc: QueryClient, userId: string): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['user-preferences', userId] }),
    qc.invalidateQueries({ queryKey: briefingStoryContextQueryKeys.all }),
  ])
}

// ─── Retry policy for auth-gated user-scoped reads ───────────────────────────
//
// Background: 2026-04-30 S9 smoke captured a "retry storm" pattern on the
// per-user GETs `/preferences/:userId` and `/saved-searches/:userId` — a
// single transient failure produced 4 attempts each (React Query's default
// `retry: 3` + initial). Auth-gated reads against a stable userId either
// work or there's a server-side bug; retrying 4 times can't fix either case
// and just amplifies cost + log noise.
//
// Policy applied to `useUserPreferences` and `useSavedSearches` below:
//   - Never retry on a 4xx (server is telling us to stop — auth, ownership,
//     missing record, validation). Retrying makes the smoke worse, not the
//     symptom better.
//   - Retry at most ONCE on a 5xx (covers a genuine transient blip — DB
//     reconnect, cold start — without producing a storm).
//   - Network-layer errors (no `ApiError`, just generic `Error`) are not
//     retried. The most common cause is "request never left the box"
//     (offline, CORS, service worker bug); retrying won't fix any of those.
//
// Other hooks in this file (articles, vulnerabilities, regulatory items,
// stories, dashboard tiles) keep React Query's default `retry: 3` because
// they're idempotent reads against shared data where transient retries
// genuinely help.
// Exported so the policy can be unit-tested in isolation
// (`apps/web/src/hooks/__tests__/queries.retry-policy.test.ts`).
export const authGatedReadRetry = (failureCount: number, error: Error): boolean => {
  if (!(error instanceof ApiError)) return false
  if (error.status >= 400 && error.status < 500) return false
  return failureCount < 1
}
import type {
  Article,
  ArticleFilters,
  KeywordWatch,
  Note,
  ProviderSummary,
  RegulatoryItem,
  SavedSearch,
  Source,
  ThreatActor,
  ThreatActorDetail,
  CampaignSummary,
  CampaignDetail,
  ThreatActorFilters,
  ThreatActorKpis,
  UserPreferences,
  VendorContentPreference,
  Vendor,
  Vulnerability,
  VulnerabilityFilters,
  RegulatoryAction,
  RegulatoryActionsStats,
  RegulatoryActionFilters,
} from '@/lib/types'

// ─── Articles ─────────────────────────────────────────────────────────────────

export function useArticles(filters?: ArticleFilters) {
  return useQuery({
    queryKey: ['articles', filters],
    queryFn: () =>
      api.get<Article[]>(`/articles${buildQueryString(filters ?? {})}`),
  })
}

export function useArticle(id: string | undefined) {
  return useQuery({
    queryKey: ['articles', id],
    queryFn: () => api.get<Article>(`/articles/${id}`),
    enabled: Boolean(id),
  })
}

export function useCreateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Article>) => api.post<Article>('/articles', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }),
  })
}

export function useUpdateArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Article> & { id: string }) =>
      api.put<Article>(`/articles/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }),
  })
}

export function useDeleteArticle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/articles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }),
  })
}

export function useToggleFlag() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isFeatured }: { id: string; isFeatured: boolean }) =>
      api.put<Article>(`/articles/${id}`, { isFeatured }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }),
  })
}

// ─── Regulatory items ─────────────────────────────────────────────────────────

export function useRegulatoryItems() {
  return useQuery({
    queryKey: ['regulatory-items'],
    queryFn: () => api.get<RegulatoryItem[]>('/regulatory-items'),
  })
}

// ─── Benchmarks ───────────────────────────────────────────────────────────────
//
// The legacy `useBenchmarks()` returning `Benchmark[]` was removed on
// 2026-04-20. All consumers moved to `useBenchmarksList()` in
// `apps/web/src/hooks/useBenchmarkQueries.ts`, which speaks the SP1 Wave-2
// `{ items, total }` envelope. Leave this block comment as a signpost so
// future `useBenchmarks` searches land here instead of failing silently.

// ─── Vulnerabilities ──────────────────────────────────────────────────────────

export function useVulnerabilities(filters?: VulnerabilityFilters) {
  return useQuery({
    queryKey: ['vulnerabilities', filters],
    queryFn: () =>
      api.get<Vulnerability[]>(
        `/vulnerabilities${buildQueryString(filters ?? {})}`,
      ),
  })
}

export function useVulnerability(id: string | undefined) {
  return useQuery({
    queryKey: ['vulnerabilities', id],
    queryFn: () => api.get<Vulnerability>(`/vulnerabilities/${id}`),
    enabled: Boolean(id),
  })
}

// Severity KPI tiles aggregated server-side (independent of LIMIT on list).
// Smoke 2026-05-03 caught Critical 50 / others 0 — page was reducing the
// LIMIT-50 list response. Backend now exposes /vulnerabilities/kpis.
export interface VulnerabilitySeverityKpis {
  critical: number
  high: number
  medium: number
  low: number
}

export function useVulnerabilitySeverityKpis() {
  return useQuery({
    queryKey: ['vulnerabilities', 'kpis'],
    queryFn: () =>
      api.get<VulnerabilitySeverityKpis>('/vulnerabilities/kpis'),
  })
}

// ─── Threat actors ────────────────────────────────────────────────────────────

export function useThreatActors(filters?: ThreatActorFilters) {
  return useQuery({
    queryKey: ['threat-actors', filters],
    queryFn: () =>
      api.get<ThreatActor[]>(
        `/threat-actors${buildQueryString(filters ?? {})}`,
      ),
  })
}

/** KAN-736 R23 (KAN-830 PR 10) — the campaign list. */
export function useCampaigns() {
  return useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<CampaignSummary[]>('/campaigns'),
  })
}

/**
 * KAN-736 R23. The API answers a non-campaign id with a 307 to the actor
 * route; fetch follows it, so the page sees `recordType !== 'campaign'` and
 * replaces the URL (R22).
 */
export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: ['campaigns', id],
    queryFn: () => api.get<CampaignDetail>(`/campaigns/${id}`),
    enabled: Boolean(id),
  })
}

export function useThreatActor(id: string | undefined) {
  return useQuery({
    queryKey: ['threat-actors', id],
    queryFn: () => api.get<ThreatActorDetail>(`/threat-actors/${id}`),
    enabled: Boolean(id),
  })
}

/**
 * Fetch aggregate KPIs for the threat-actors page tile rail.
 *
 * Accepts the same filter shape as `useThreatActors` (minus `sort`, which has
 * no effect on aggregates). Returns corpus-wide counts so the rail does not
 * saturate at the list endpoint's DEFAULT_LIMIT — see
 * `apps/api/src/routes/threat-actors.ts` `GET /kpis` for the contract.
 */
export function useThreatActorKpis(
  filters?: Pick<ThreatActorFilters, 'attribution'>,
) {
  return useQuery({
    queryKey: ['threat-actors-kpis', filters],
    queryFn: () =>
      api.get<ThreatActorKpis>(
        `/threat-actors/kpis${buildQueryString(filters ?? {})}`,
      ),
  })
}

// ─── Notes ───────────────────────────────────────────────────────────────────

export interface NoteParams {
  [key: string]: string | undefined
  articleId?: string
  regulatoryItemId?: string
  benchmarkId?: string
  vulnerabilityId?: string
}

export function useNotes(params?: NoteParams) {
  return useQuery({
    queryKey: ['notes', params],
    queryFn: () =>
      api.get<Note[]>(`/notes${buildQueryString(params ?? {})}`),
    enabled: Boolean(params && Object.values(params).some(Boolean)),
  })
}

export function useCreateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      contentType: string
      contentId: string
      authorId: string
      text: string
    }) => api.post<Note>('/notes', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }),
  })
}

export function useUpdateNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; text?: string; isFlagged?: boolean }) =>
      api.put<Note>(`/notes/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }),
  })
}

export function useDeleteNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/notes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notes'] }),
  })
}

// ─── Vendors ─────────────────────────────────────────────────────────────────

/**
 * The vendor directory's FIRST PAGE only (the API defaults to 50 names). Fine
 * for admin tables that page themselves; a reader picker must not rely on it —
 * use useVendorsByIds for a saved selection and useVendorSearch to add more
 * (KAN-822 P2).
 */
export function useVendors() {
  return useQuery({
    queryKey: ['vendors'],
    queryFn: () => api.get<Vendor[]>('/vendors'),
  })
}

/** KAN-822 P2: the API's `?ids=` cap (MAX_VENDOR_LOOKUP_IDS in apps/api/src/routes/vendors.ts). */
export const VENDOR_LOOKUP_CHUNK = 100
/** The API's cap on saved Vendor updates subject vendors (MAX_VENDOR_SUBJECT_IDS in apps/api/src/routes/preferences.ts). */
export const VENDOR_SUBJECT_MAX = 100
/** KAN-822 P2: one page of picker search results. */
export const VENDOR_SEARCH_PAGE = 50
/** The API's cap on a vendor search, after trimming (MAX_VENDOR_SEARCH_LENGTH in apps/api/src/routes/vendors.ts). */
export const VENDOR_SEARCH_MAX_LENGTH = 200

/**
 * KAN-822 P2 — resolve vendors BY ID (`GET /vendors?ids=`), wherever they sort,
 * so a saved selection is always present in a picker. Ids with no vendor row
 * are simply absent from the result. Long lists are sent in chunks of the
 * API's cap. No ids: no request, an empty result.
 */
export function useVendorsByIds(ids: readonly string[]) {
  const sorted = [...new Set(ids.map((id) => id.toLowerCase()))].sort()
  return useQuery({
    queryKey: ['vendors', 'by-ids', sorted],
    queryFn: async () => {
      if (sorted.length === 0) return [] as Vendor[]
      const chunks: string[][] = []
      for (let i = 0; i < sorted.length; i += VENDOR_LOOKUP_CHUNK) {
        chunks.push(sorted.slice(i, i + VENDOR_LOOKUP_CHUNK))
      }
      const pages = await Promise.all(
        chunks.map((chunk) => api.get<Vendor[]>(`/vendors${buildQueryString({ ids: chunk.join(',') })}`)),
      )
      return pages.flat()
    },
  })
}

/**
 * KAN-822 P2 — server-side vendor search for a picker (`GET /vendors?q=`, name
 * or alias, one page). An empty query browses the first page. A query longer
 * than VENDOR_SEARCH_MAX_LENGTH is clipped (the pickers' inputs stop there too).
 *
 * No placeholder data: while a new query loads the result is pending with no
 * data, so a success always holds THIS query's results and a picker can never
 * act on the previous query's page. Pickers also hide results while their
 * typed filter is ahead of the debounced query (see useVendorPickerSearch).
 */
export function useVendorSearch(query: string) {
  // Clipped to the API's cap so an over-long search never turns into a failure.
  const q = query.trim().slice(0, VENDOR_SEARCH_MAX_LENGTH).trim()
  return useQuery({
    queryKey: ['vendors', 'search', q],
    queryFn: () => api.get<Vendor[]>(`/vendors${buildQueryString({ q, limit: VENDOR_SEARCH_PAGE })}`),
  })
}

export function useCreateVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Vendor>) => api.post<Vendor>('/vendors', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

export function useUpdateVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Vendor> & { id: string }) =>
      api.put<Vendor>(`/vendors/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

export function useDeleteVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/vendors/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  })
}

// ─── User preferences ─────────────────────────────────────────────────────────

/**
 * Server envelope for `GET /api/preferences/:userId`. The route lives at
 * `/preferences` (mounted in `apps/api/src/routes/index.ts`) and returns four
 * tables — preferences, trackedVendors join rows, keywordWatches, savedSearches
 * — joined client-side. The hook below flattens the envelope into the
 * `UserPreferences` shape consumers (`usePersonalisedScore`, `DashboardPage`,
 * Settings sections) already use, so callers can read `prefs.trackedVendors` /
 * `prefs.keywordWatches` directly.
 *
 * `PUT /api/preferences/:userId` returns the bare row (no envelope), which
 * matches `UserPreferences` directly modulo the colocated array fields — those
 * stay populated from the GET cache via React Query invalidation.
 */
interface UserPreferencesEnvelope {
  preferences:
    | (Omit<UserPreferences, 'trackedVendors' | 'keywordWatches'> & {
        trackedVendors?: never
        keywordWatches?: never
      })
    | null
  trackedVendors: Array<{ userId: string; vendorId: string }>
  keywordWatches: KeywordWatch[]
  savedSearches: SavedSearch[]
  /** KAN-822 T3 — additive; absent on older API builds. */
  vendorContent?: VendorContentPreference
}

export function useUserPreferences(userId: string) {
  return useQuery({
    queryKey: ['user-preferences', userId],
    queryFn: async (): Promise<UserPreferences | null> => {
      const envelope = await api.get<UserPreferencesEnvelope>(
        `/preferences/${userId}`,
      )
      if (!envelope.preferences) return null
      return {
        ...envelope.preferences,
        trackedVendors: envelope.trackedVendors.map((t) => t.vendorId),
        keywordWatches: envelope.keywordWatches,
        ...(envelope.vendorContent ? { vendorContent: envelope.vendorContent } : {}),
      }
    },
    enabled: Boolean(userId),
    retry: authGatedReadRetry,
  })
}

/**
 * KAN-822 T9 — save the reader's Vendor updates preference.
 *
 * Sends ONLY the fields the reader changed (the API treats each as a partial
 * update, so turning it off keeps the narrowing and narrowing never opts in).
 * On success it invalidates everything the preference shapes: the reader's own
 * preferences, the story family (feed, search, detail, analytics), the
 * dashboard tiles and the article feed. Nothing is invalidated on failure.
 */
export function useSaveVendorContent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, vendorContent }: { userId: string; vendorContent: Partial<VendorContentPreference> }) =>
      api.put<UserPreferences>(`/preferences/${userId}`, { vendorContent }),
    onSuccess: (_data, vars) =>
      Promise.all([
        invalidateRelevanceInputs(qc, vars.userId),
        qc.invalidateQueries({ queryKey: storyQueryKeys.all }),
        qc.invalidateQueries({ queryKey: ['dashboard'] }),
        qc.invalidateQueries({ queryKey: ['articles'] }),
      ]),
  })
}

export function useUpdateUserPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: Partial<UserPreferences> & { userId: string }) =>
      api.put<UserPreferences>(`/preferences/${userId}`, data),
    onSuccess: (_data, vars) => invalidateRelevanceInputs(qc, vars.userId),
  })
}

/**
 * KAN-939 — the reader's timezone (`users.timezone`), which the briefing
 * scheduler sends by. Its own route and query key: it lives on the `users`
 * row, not the preferences row, so it is not part of `useUserPreferences`.
 */
export function useUserTimezone(userId: string) {
  return useQuery({
    queryKey: ['user-timezone', userId],
    queryFn: () => api.get<{ timezone: string }>(`/preferences/${userId}/timezone`),
    enabled: Boolean(userId),
    retry: authGatedReadRetry,
  })
}

/** KAN-939 — saves the reader's timezone. Only ever called from an explicit Save. */
export function useUpdateUserTimezone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, timezone }: { userId: string; timezone: string }) =>
      api.put<{ timezone: string }>(`/preferences/${userId}/timezone`, { timezone }),
    // The server stores its canonical spelling (`europe/athens` ->
    // `Europe/Athens`). Put that in the read cache straight away so the
    // Settings field shows what was stored, then re-read in the background
    // (not awaited: the save is done once the server has answered).
    onSuccess: (data, vars) => {
      qc.setQueryData(['user-timezone', vars.userId], data)
      void qc.invalidateQueries({ queryKey: ['user-timezone', vars.userId] })
    },
  })
}

/**
 * KAN-32 F1 — acknowledges the in-app analytics notice.
 *
 * A dedicated hook rather than a call through `useUpdateUserPreferences`
 * because the wire field (`analyticsNoticeAcknowledged: true`) is not a
 * `UserPreferences` field: the server translates it into a timestamp it
 * generates itself, so it is deliberately absent from the read type.
 *
 * It invalidates `['user-preferences', userId]` — the SAME key
 * `useUserPreferences` reads, which is the key the notice component reads.
 * That is the whole point. The Art. 21 opt-out shipped with its storage and
 * its enforcement point on two different query keys, and the objection took
 * effect only after a page reload (error log 2026-07-26). One fact, one key.
 */
export function useAcknowledgeAnalyticsNotice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      api.put<UserPreferences>(`/preferences/${userId}`, {
        analyticsNoticeAcknowledged: true,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['user-preferences', vars.userId] }),
  })
}

/**
 * KAN-904 — the member's own analytics objection (KAN-519 suppression).
 *
 * `objected: true` sends `analyticsSuppressed: true`, which the server turns
 * into `analytics_suppressed_at` (first time kept); `false` clears it. The wire
 * field is a boolean the server owns as a timestamp, so it is not a
 * `UserPreferences` field and this is a dedicated hook, like the two around it.
 *
 * Invalidates on SETTLED, not just success: after a failed save the switch must
 * show what the server actually holds, never the position the member clicked.
 */
export function useSetAnalyticsObjection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, objected }: { userId: string; objected: boolean }) =>
      api.put<UserPreferences>(`/preferences/${userId}`, {
        analyticsSuppressed: objected,
      }),
    onSettled: (_data, _err, vars) =>
      qc.invalidateQueries({ queryKey: ['user-preferences', vars.userId] }),
  })
}

/**
 * KAN-836 — turns the weekly regulatory digest on or off for the signed-in
 * user, which is how someone who used the digest's one-click unsubscribe gets
 * it back.
 *
 * A dedicated hook for the same reason as `useAcknowledgeAnalyticsNotice`: the
 * wire field (`regulatoryDigestEnabled`) is a boolean the server turns into
 * its own `regulatoryDigestOptedOutAt` timestamp, so it is not a
 * `UserPreferences` field. It invalidates the key `useUserPreferences` reads,
 * so the switch shows the saved state without a reload.
 */
export function useSetRegulatoryDigestSubscription() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) =>
      api.put<UserPreferences>(`/preferences/${userId}`, {
        regulatoryDigestEnabled: enabled,
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['user-preferences', vars.userId] }),
  })
}

// ─── Providers (supply chain) ────────────────────────────────────────────────

/**
 * Lightweight list of supply-chain / cloud providers, used by the muted-
 * providers chip input in Settings (Plan C Plan 2 T22). Returns the raw
 * `providers` array unwrapped from the API envelope.
 */
export function useProviders() {
  return useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const res = await api.get<{ providers: ProviderSummary[] }>('/providers')
      return res.providers
    },
    // Provider list is effectively static — cache aggressively.
    staleTime: 5 * 60 * 1000,
  })
}

// ─── Saved searches ───────────────────────────────────────────────────────────
//
// All three CRUD hooks live under `/api/preferences/:userId/searches` —
// see `apps/api/src/routes/preferences.ts`. The previous flat `/saved-searches`
// shape never had a server handler and silently 404'd in production
// (incident 2026-04-30, S9 smoke). Keeping the URLs co-located with the rest
// of the per-user preference CRUD lets the server share its ownership +
// uuid-resolution gate.

export function useSavedSearches(userId: string) {
  return useQuery({
    queryKey: ['saved-searches', userId],
    queryFn: () => api.get<SavedSearch[]>(`/preferences/${userId}/searches`),
    enabled: Boolean(userId),
    retry: authGatedReadRetry,
  })
}

export function useCreateSavedSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      ...data
    }: { userId: string; name: string; filters: Record<string, unknown> }) =>
      api.post<SavedSearch>(`/preferences/${userId}/searches`, data),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['saved-searches', vars.userId] }),
  })
}

export function useDeleteSavedSearch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, id }: { userId: string; id: string }) =>
      api.delete<void>(`/preferences/${userId}/searches/${id}`),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['saved-searches', vars.userId] }),
  })
}

// ─── Keyword watches ──────────────────────────────────────────────────────────
//
// Surfaced through the `useUserPreferences` envelope (`prefs.keywordWatches`).
// Wave-1 P5 settings primitive uses these for the keyword-watch input UI.

export function useCreateKeywordWatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      keyword,
      boostWeight,
    }: {
      userId: string
      keyword: string
      boostWeight?: number
    }) =>
      api.post<KeywordWatch>(`/preferences/${userId}/keywords`, {
        keyword,
        boostWeight,
      }),
    onSuccess: (_data, vars) => invalidateRelevanceInputs(qc, vars.userId),
  })
}

export function useDeleteKeywordWatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, id }: { userId: string; id: string }) =>
      api.delete<void>(`/preferences/${userId}/keywords/${id}`),
    onSuccess: (_data, vars) => invalidateRelevanceInputs(qc, vars.userId),
  })
}

// ─── Tracked vendors ──────────────────────────────────────────────────────────

export function useTrackVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, vendorId }: { userId: string; vendorId: string }) =>
      api.post<{ tracked: true }>(`/preferences/${userId}/vendors`, {
        vendorId,
      }),
    onSuccess: (_data, vars) => invalidateRelevanceInputs(qc, vars.userId),
  })
}

export function useUntrackVendor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, vendorId }: { userId: string; vendorId: string }) =>
      api.delete<void>(`/preferences/${userId}/vendors/${vendorId}`),
    onSuccess: (_data, vars) => invalidateRelevanceInputs(qc, vars.userId),
  })
}

// ─── Sources (admin) ─────────────────────────────────────────────────────────

export function useSources() {
  return useQuery({
    queryKey: ['sources'],
    queryFn: () => api.get<Source[]>('/sources'),
  })
}

export function useCreateSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Source>) => api.post<Source>('/sources', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  })
}

export function useUpdateSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Source> & { id: string }) =>
      api.put<Source>(`/sources/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  })
}

export function useDeleteSource() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/sources/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  })
}

export function useTriggerIngest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ jobId: string }>(`/sources/${id}/ingest`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sources'] }),
  })
}

// ─── Regulatory Actions ──────────────────────────────────────────────────────

export function useRegulatoryActions(filters?: RegulatoryActionFilters) {
  return useQuery({
    queryKey: ['regulatory-actions', filters],
    queryFn: () =>
      api.get<{ data: RegulatoryAction[]; pagination: { page: number; limit: number; total: number } }>(
        `/regulatory-actions${buildQueryString(filters ?? {})}`
      ),
  })
}

export function useRegulatoryActionsStats() {
  return useQuery({
    queryKey: ['regulatory-actions-stats'],
    queryFn: () => api.get<RegulatoryActionsStats>('/regulatory-actions/stats'),
  })
}
